"use client"

import { useState, useEffect } from "react"
import { useRouter, usePathname } from "next/navigation"
import { CRM_API_BASE } from "@/lib/config"
import { httpJson } from "@/lib/http"
import {
  ExternalLink,
  LinkIcon,
  Search,
  AlertTriangle,
  Plus,
  LogOut,
  User,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  ChevronRight,
  ChevronDown,
  Loader2,
  X,
} from "lucide-react"
import { DashboardIcon } from "@/components/dashboard-icon"
import Link from "next/link"
import { useAuth } from "@/components/auth-provider"
import { Separator } from "@/components/ui/separator"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { CreateOrderForm } from "@/components/create-order-form"
import { ClientFilterDropdown } from "@/components/client-filter-dropdown"
import { BusinessFilterDropdown } from "@/components/business-filter-dropdown"
import { UserFilterDropdown } from "@/components/user-filter-dropdown"
import { Label } from "@/components/ui/label"
import { OrdersTable } from "@/components/orders-table"
import { useColumnToggle } from "@/hooks/use-column-toggle"
import { DataTableColumnToggle } from "@/components/data-table-column-toggle"
import { GlobalPagination } from "@/components/global-pagination"
import { ClientForm } from "@/components/client-form"
import { buildCreateInternalClientUserPayload, postInternalClientUser } from "@/lib/client-primary-user"
import { AddBusinessModal } from "@/components/add-business-modal"
import { AddOrganizationModal } from "@/components/add-organization-modal"
import { AddOpportunityModal } from "@/components/add-opportunity-modal"
import { isConnectionDeleted, type ApiOrderLike } from "@/lib/order-table-utils"
import { useToast } from "@/hooks/use-toast"

const statusFilterMap: Record<string, number> = {
  created: 0,
  in_progress: 1,
  paused: 2,
  cancelled: 3,
  complete: 4,
}

interface ApiOrder {
  id: string | number
  client?: {
    id: string | number
    company_name?: string
    first_name?: string
    last_name?: string
    email?: string
  }
  businesses?: Array<{
    id: string | number
    name: string
  }>
  client_name?: string
  business_id?: string | number
  stage?: string
  status?: string
  due_date?: string
  link_count?: number
  links_built?: number
  links_pending?: number
  complete?: number
  [key: string]: any
}

interface PaginationInfo {
  count: number
  next: string | null
  previous: string | null
  total_pages?: number
  current_page?: number
}

interface ApiResponse {
  results: ApiOrder[]
  count: number
  next: string | null
  previous: string | null
  total_pages?: number
  current_page?: number
}

export default function Dashboard() {
  const { user, logout } = useAuth()
  const router = useRouter()
  const { toast } = useToast()
  const pathname = usePathname()

  const [searchQuery, setSearchQuery] = useState("")
  const [searchInputValue, setSearchInputValue] = useState("")
  const [statusFilter, setStatusFilter] = useState("all")
  const [clientFilter, setClientFilter] = useState("all")
  const [businessFilter, setBusinessFilter] = useState("all")
  const [userFilter, setUserFilter] = useState("all")
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false)
  const [duplicateOrder, setDuplicateOrder] = useState<any>(null)
  const [isAddingClient, setIsAddingClient] = useState(false)
  const [isAddingBusiness, setIsAddingBusiness] = useState(false)
  const [isAddingOrganization, setIsAddingOrganization] = useState(false)
  const [isAddingOpportunity, setIsAddingOpportunity] = useState(false)
  const [apiOrders, setApiOrders] = useState<ApiOrder[]>([])
  const [isLoadingOrders, setIsLoadingOrders] = useState(true)
  const [ordersError, setOrdersError] = useState<string | null>(null)
  const [displayedOrders, setDisplayedOrders] = useState<ApiOrder[]>([])
  const [paginationLoading, setPaginationLoading] = useState(false)
  const [initialOrders, setInitialOrders] = useState<ApiOrder[]>([])
  const [initialOrdersCount, setInitialOrdersCount] = useState(0)
  const [totalOrdersCount, setTotalOrdersCount] = useState(0)

  const [activeClientsCount, setActiveClientsCount] = useState<number | null | "failed">(null)
  const [isLoadingClients, setIsLoadingClients] = useState(true)
  const [organizationsCount, setOrganizationsCount] = useState<number | null | "failed">(null)
  const [opportunitiesCount, setOpportunitiesCount] = useState<number | null | "failed">(null)
  const [businessesCount, setBusinessesCount] = useState<number | null | "failed">(null)
  const [ordersCountByStatus, setOrdersCountByStatus] = useState<Record<string, number | "failed">>({
    created: 0,
    in_progress: 0,
    paused: 0,
    cancelled: 0,
    complete: 0,
  })

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [nextPageUrl, setNextPageUrl] = useState<string | null>(null)
  const [previousPageUrl, setPreviousPageUrl] = useState<string | null>(null)

  // Collapsible states
  const [actionRequiredOpen, setActionRequiredOpen] = useState(true)
  const [pipelineStatusOpen, setPipelineStatusOpen] = useState(true)
  const [todaysTasksOpen, setTodaysTasksOpen] = useState(true)
  const [ordersOpen, setOrdersOpen] = useState(true)

  const { columns, handleColumnChange, handleSelectAllColumns } = useColumnToggle({
    initialColumns: [
      { id: "order_id", label: "Order ID", isVisible: true, isAlwaysVisible: true },
      { id: "client", label: "Client", isVisible: true },
      { id: "business", label: "Business", isVisible: true },
      { id: "user_email", label: "User", isVisible: true },
      { id: "recurrence", label: "Recurrence", isVisible: true },
      { id: "locations", label: "Locations", isVisible: true },
      { id: "combo_configuration", label: "Combo Configuration", isVisible: true },
      { id: "links_progress", label: "Links Progress", isVisible: true },
      { id: "status", label: "Status", isVisible: true },
      { id: "dates", label: "Dates", isVisible: true },
      { id: "paid", label: "Paid", isVisible: true },
      { id: "actions", label: "Actions", isVisible: true },
    ],
    enablePin: false,
  })

  const pipelineData = {
    newOppsReady: 12,
    needingValidation: 8,
    autoOutreachActive: 15,
    rejectedToday: 5,
    totalProcessedToday: 40,
  }

  const [mockOrders, setMockOrders] = useState([
    {
      id: "ORD-001",
      client: "TechFlow AI",
      campaign: "Q1 Link Building",
      linksTarget: 50,
      linksBuilt: 35,
      linksPending: 15,
      dueDate: "2024-01-20",
      estimatedCompletion: "2024-01-19",
      status: "In Progress",
      priority: "High",
      daysRemaining: 3,
    },
    {
      id: "ORD-002",
      client: "DataMind Corp",
      campaign: "Brand Authority Campaign",
      linksTarget: 30,
      linksBuilt: 30,
      linksPending: 0,
      dueDate: "2024-01-18",
      estimatedCompletion: "2024-01-18",
      status: "Completed",
      priority: "High",
      daysRemaining: 0,
    },
    {
      id: "ORD-003",
      client: "Neural Dynamics",
      campaign: "Product Launch Links",
      linksTarget: 75,
      linksBuilt: 25,
      linksPending: 50,
      dueDate: "2024-01-25",
      estimatedCompletion: "2024-01-28",
      status: "Behind Schedule",
      priority: "Medium",
      daysRemaining: 8,
    },
  ])

  const [tasks] = useState([
    {
      id: "TSK-001",
      type: "call",
      title: "Follow-up call with TechFlow AI",
      description: "Discuss Q1 campaign progress and potential expansion",
      client: "TechFlow AI",
      dueDate: "2024-01-17",
      priority: "High",
      status: "Pending",
      relatedTo: "ORD-001",
      estimatedDuration: "30 min",
      assignedTo: "John Smith",
      tags: ["follow-up", "client", "expansion"],
    },
    {
      id: "TSK-002",
      type: "email",
      title: "Send weekly report to Neural Dynamics",
      description: "Weekly progress report for Product Launch Links campaign",
      client: "Neural Dynamics",
      dueDate: "2024-01-17",
      priority: "Medium",
      status: "Pending",
      relatedTo: "ORD-003",
      estimatedDuration: "15 min",
      assignedTo: "Sarah Johnson",
      tags: ["report", "weekly", "progress"],
    },
  ])

  const safeStringIncludes = (value: any, searchTerm: string): boolean => {
    if (value === null || value === undefined) return false
    return String(value).toLowerCase().includes(searchTerm.toLowerCase())
  }

  const safeStringValue = (value: any): string => {
    if (value === null || value === undefined) return ""
    if (typeof value === "string") return value
    if (typeof value === "number") return String(value)
    if (typeof value === "object") {
      if (value.name) return String(value.name)
      if (value.title) return String(value.title)
      if (value.id) return String(value.id)
      return JSON.stringify(value)
    }
    return String(value)
  }

  const safeNumericValue = (value: any): number => {
    if (value === null || value === undefined) return 0
    if (typeof value === "number") return value
    if (typeof value === "string") {
      const parsed = Number.parseInt(value, 10)
      return isNaN(parsed) ? 0 : parsed
    }
    return 0
  }

  // Helper function for single retry
  const retryOnce = async <T,>(
    fn: () => Promise<T>,
    delay = 2000
  ): Promise<T | "failed"> => {
    try {
      return await fn()
    } catch (error) {
      // Don't retry if request was cancelled
      if (error instanceof Error && error.message === "Request was cancelled") {
        throw error
      }
      
      // Single retry after delay
      console.log(`[v0] Request failed, retrying once after ${delay}ms`)
      await new Promise(resolve => setTimeout(resolve, delay))
      
      try {
        return await fn()
      } catch (retryError) {
        console.warn(`[v0] Retry also failed, marking as failed`)
        return "failed"
      }
    }
  }

  const getAuthToken = () => {
    if (typeof window === "undefined") {
      console.log("🔍 [v0] getAuthToken: window is undefined (SSR)")
      return null
    }

    const token = localStorage.getItem("auth_token")
    console.log("🔍 [v0] getAuthToken: token exists:", !!token)
    console.log("🔍 [v0] getAuthToken: token preview:", token ? token.substring(0, 20) + "..." : "none")
    console.log("🔍 [v0] getAuthToken: localStorage keys:", Object.keys(localStorage))

    return token
  }

  const buildApiUrl = (page: number, size: number, search?: string, status?: string, client?: string, business?: string, userId?: string) => {
    const offset = (page - 1) * size
    const params = new URLSearchParams({
      limit: size.toString(),
      offset: offset.toString(),
    })

    if (search) {
      const trimmed = search.trim()
      if (/^\d+$/.test(trimmed)) {
        params.append("id", trimmed)
      } else if (trimmed) {
        params.append("q", trimmed)
      }
    }
    if (status && status !== "all" && statusFilterMap[status] !== undefined) {
      params.append("status", statusFilterMap[status].toString())
    }
    if (client && client !== "all") {
      params.append("client_id", client)
    }
    if (business && business !== "all") {
      params.append("business__id", business)
    }
    if (userId && userId !== "all") {
      params.append("users", userId)
    }
    params.append("ordering", "-date_order")

    return `${CRM_API_BASE}/client_order/?${params.toString()}`
  }

  const fetchOrders = async (page = 1, size = 10, isPaginationChange = false, search?: string, status?: string, client?: string, business?: string, userId?: string) => {
    console.log("🔍 [v0] fetchOrders called with:", { page, size, isPaginationChange, search, status, client, business, userId })
    console.log("🔍 [v0] fetchOrders user exists:", !!user)
    console.log("🔍 [v0] fetchOrders user email:", user?.email)

    if (!user) {
      console.log("⚠️ [v0] fetchOrders: No user, setting loading to false")
      setIsLoadingOrders(false)
      return
    }

    if (user.role === "org" || user.role === "organization" || user.role === "client") {
      console.log("⚠️ [v0] fetchOrders: Non-admin user, skipping admin API calls")
      setIsLoadingOrders(false)
      return
    }

    try {
      if (isPaginationChange || search || (status && status !== "all") || (client && client !== "all") || (business && business !== "all") || (userId && userId !== "all")) {
        setPaginationLoading(true)
      } else {
        setIsLoadingOrders(true)
      }
      setOrdersError(null)

      let token = getAuthToken()

      if (!token && user?.token) {
        console.log("🔄 [v0] fetchOrders: Using token from user context as fallback")
        token = user.token
      }

      if (!token) {
        console.error("❌ [v0] fetchOrders: No token found in localStorage or user context")
        console.error("❌ [v0] fetchOrders: User state:", user)
        console.error("❌ [v0] fetchOrders: localStorage contents:", Object.keys(localStorage))
        throw new Error("No authentication token found. Please log in again.")
      }

      console.log("✅ [v0] fetchOrders: Token found, proceeding with API call")

      const apiUrl = buildApiUrl(page, size, search, status, client, business, userId)
      console.log("🔍 [v0] Fetching orders from URL:", apiUrl)

      try {
        const rawData = await httpJson(
          apiUrl,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
              "User-Agent": "CRM-Dashboard/1.0",
            },
          },
          60000,
        )

        const data: ApiResponse = {
          results: rawData.results || rawData || [],
          count: rawData.count || 0,
          next: rawData.next || null,
          previous: rawData.previous || null,
          total_pages: rawData.total_pages || Math.ceil((rawData.count || 0) / size),
          current_page: rawData.current_page || page,
          limit: size,
          offset: (page - 1) * size,
        }

        const ordersArray = data.results || data || []

        console.log("[v0] Raw order data sample:", ordersArray[0])
        console.log("[v0] Available fields in first order:", ordersArray[0] ? Object.keys(ordersArray[0]) : "No orders")
        console.log("[v0] First order connections:", ordersArray[0]?.connections)
        console.log("[v0] First order link_count:", ordersArray[0]?.link_count)
        console.log("[v0] First order complete:", ordersArray[0]?.complete)

        const processedOrders = ordersArray.map((order: any, index: number) => {
          console.log(
            `[v0] Order ${index} - link_count: ${order.link_count}, complete: ${order.complete}, completed_sponsorships: ${order.completed_sponsorships}, links_built: ${order.links_built}`,
          )

          let linkCount = 0
          let completeCount = 0

          if (order.connections && Array.isArray(order.connections)) {
            const activeConnections = order.connections.filter((conn: any) => !isConnectionDeleted(conn))
            linkCount = activeConnections.length
            completeCount = activeConnections.filter((conn: any) => {
              const status = conn?.status || conn?.stage
              return (
                status === "complete" ||
                status === "completed" ||
                status === "fulfilled" ||
                status === 4 || // Handle numeric stage value for complete
                status === "4" // Handle string numeric stage value
              )
            }).length
          }

            return {
            ...order,
            id: safeStringValue(order.id || `order-${index}`),
            business_id: safeStringValue(order.business_id || order.businessId || ""),
            status: safeStringValue(order.status || order.state || "Unknown"),
            due_date: safeStringValue(order.due_date || order.dueDate || ""),
            businesses: order.businesses || [],
            link_count: safeNumericValue(linkCount || order.link_count || order.linkCount || order.links_target || 0),
            complete: safeNumericValue(
              completeCount || order.complete || order.completed_sponsorships || order.links_built || 0,
            ),
            links_built: safeNumericValue(order.links_built || order.linksBuilt || 0),
            links_pending: safeNumericValue(order.links_pending || order.linksPending || 0),
          }
        })

        console.log("[v0] Processed order sample:", processedOrders[0])

        setApiOrders(processedOrders)

        if (!isPaginationChange) {
          setDisplayedOrders(processedOrders)
        }

        const hasNoFilters = !search && (!status || status === "all") && (!client || client === "all")
        if (hasNoFilters && !isPaginationChange && page === 1) {
          setInitialOrders(processedOrders)
          setInitialOrdersCount(data.count || 0)
          setTotalOrdersCount(data.count || 0)
        }

        const newTotalCount = data.count || 0
        const newTotalPages = data.total_pages || Math.ceil((rawData.count || 0) / size)
        const newCurrentPage = data.current_page || page

        setTotalCount(newTotalCount)
        setTotalPages(newTotalPages)
        setCurrentPage(newCurrentPage)
        setNextPageUrl(data.next)
        setPreviousPageUrl(data.previous)
      } catch (error) {
        if (error instanceof Error && error.message === "Request was cancelled") {
          console.log("[v0] Orders fetch was cancelled (likely due to logout)")
          return
        }

        console.error("❌ DASHBOARD: Error fetching orders:", error instanceof Error ? error.message : "Unknown error")

        if (error instanceof Error && error.message.includes("authentication")) {
          console.log("🔄 [v0] Authentication error detected, clearing auth state")
          localStorage.removeItem("auth_token")
          localStorage.removeItem("refresh_token")
          localStorage.removeItem("user_data")
        }

        if (error instanceof Error) {
          if (error.name === "AbortError") {
            setOrdersError("Request timed out after 60 seconds. The external API is slow, please wait or try again.")
          } else if (error.message.includes("Failed to fetch") || error.message.includes("NetworkError")) {
            setOrdersError("Network error. Please check your internet connection and try again.")
          } else if (error.message.includes("TypeError")) {
            setOrdersError("Connection error. Please check if the server is accessible.")
          } else if (error.message.includes("HTTP 500") && error.message.includes("OperationalError")) {
            setOrdersError(
              "Backend database error. The CRM system has a database issue - please contact your system administrator.",
            )
          } else if (error.message.includes("HTTP 500") && (error.message.includes("AnchorText") || error.message.includes("'url'"))) {
            setOrdersError(
              "Backend data issue detected. Some orders may have incomplete data. Please contact support to fix this issue.",
            )
            setApiOrders([])
            setDisplayedOrders([])
          } else if (error.message.includes("Unknown column")) {
            setOrdersError(
              "Database schema mismatch. The backend database structure needs to be updated - please contact your system administrator.",
            )
          } else if (error.message.includes("HTTP 500")) {
            setOrdersError(
              "Server error. The CRM backend is experiencing issues - please try again later or contact support.",
            )
          } else {
            setOrdersError(error.message)
          }
        } else {
          setOrdersError("An unexpected error occurred while fetching orders.")
        }
      }
    } finally {
      if (isPaginationChange || search || (status && status !== "all") || (client && client !== "all") || (business && business !== "all") || (userId && userId !== "all")) {
        setPaginationLoading(false)
      } else {
        setIsLoadingOrders(false)
      }
    }
  }

  const fetchOrganizationsCount = async () => {
    if (!user?.token) {
      console.log("[v0] No token available for organizations API call")
      return
    }

    if (user.role === "org" || user.role === "organization" || user.role === "client") {
      console.log("[v0] Non-admin user, skipping organizations count API call")
      return
    }
    
    const result = await retryOnce(async () => {
      console.log("[v0] Fetching organizations count from:", `${CRM_API_BASE}/org/?limit=1`)
      const data = await httpJson(`${CRM_API_BASE}/org/?limit=1`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      console.log("[v0] Organizations API response:", data)
      return data.count ?? 0
    })
    
    setOrganizationsCount(result)
  }

  const fetchOpportunitiesCount = async () => {
    if (!user?.token) {
      console.log("[v0] No token available for opportunities API call")
      return
    }

    if (user.role === "org" || user.role === "organization" || user.role === "client") {
      console.log("[v0] Non-admin user, skipping opportunities count API call")
      return
    }
    
    const result = await retryOnce(async () => {
      console.log("[v0] Fetching opportunities count from:", `${CRM_API_BASE}/opp/?limit=1`)
      const data = await httpJson(`${CRM_API_BASE}/opp/?limit=1`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      console.log("[v0] Opportunities API response:", data)
      return data.count ?? 0
    })
    
    setOpportunitiesCount(result)
  }

  const fetchBusinessesCount = async () => {
    if (!user?.token) {
      console.log("[v0] No token available for businesses API call")
      return
    }

    if (user.role === "org" || user.role === "organization" || user.role === "client") {
      console.log("[v0] Non-admin user, skipping businesses count API call")
      return
    }
    
    const result = await retryOnce(async () => {
      console.log("[v0] Fetching businesses count from:", `${CRM_API_BASE}/business/?limit=1`)
      const data = await httpJson(`${CRM_API_BASE}/business/?limit=1`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      console.log("[v0] Businesses API response:", data)
      return data.count ?? 0
    })
    
    setBusinessesCount(result)
  }

  const fetchClientsCount = async () => {
    if (!user?.token) {
      console.log("[v0] No token available for clients count API call")
      return
    }

    if (user.role === "org" || user.role === "organization" || user.role === "client") {
      console.log("[v0] Non-admin user, skipping clients count API call")
      return
    }
    
    const result = await retryOnce(async () => {
      console.log("[v0] Fetching clients count from:", `${CRM_API_BASE}/client/?limit=1`)
      const data = await httpJson(`${CRM_API_BASE}/client/?limit=1`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      })
      console.log("[v0] Clients API response:", data)
      return data.count ?? 0
    })
    
    setActiveClientsCount(result)
  }

  const fetchOrdersCountByStatus = async () => {
    if (!user?.token) {
      console.log("[v0] No token available for orders count by status API call")
      return
    }

    if (user.role === "org" || user.role === "organization" || user.role === "client") {
      console.log("[v0] Non-admin user, skipping orders count by status API call")
      return
    }

    const statuses = [
      { key: "created", status: 0, label: "Created" },
      { key: "in_progress", status: 1, label: "In Progress" },
      { key: "paused", status: 2, label: "Paused" },
      { key: "cancelled", status: 3, label: "Cancelled" },
      { key: "complete", status: 4, label: "Completed" },
    ]

    const counts: Record<string, number | "failed"> = {}

    // Fetch all counts in parallel, each with its own independent retry
    const promises = statuses.map(async ({ key, status, label }) => {
      const result = await retryOnce(async () => {
        const data = await httpJson(`${CRM_API_BASE}/client_order/?limit=1&status=${status}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        })
        return { key, count: data.count ?? 0 }
      })
      
      // If retry failed, mark as "failed"
      return result === "failed" ? { key, count: "failed" as const } : result
    })

    const results = await Promise.all(promises)
    results.forEach(({ key, count }) => {
      counts[key] = count
    })

    setOrdersCountByStatus(counts)
  }


  useEffect(() => {
    const loadData = async () => {
      setIsLoadingOrders(true)
      setIsLoadingClients(true)
      try {
        await Promise.all([fetchOrders(), fetchOrganizationsCount(), fetchOpportunitiesCount(), fetchBusinessesCount(), fetchOrdersCountByStatus()])
      } catch (error) {
        if (error instanceof Error && error.message === "Request was cancelled") {
          console.log("[v0] Dashboard data load was cancelled (likely due to logout)")
          return
        }
        setOrdersError("Failed to load dashboard data")
      } finally {
        setIsLoadingOrders(false)
        setIsLoadingClients(false)
      }
    }

    loadData()
  }, [])

  useEffect(() => {
    let cancelled = false
    httpJson<{ count?: number }>(`${CRM_API_BASE}/client/?limit=1`)
      .then((data) => {
        if (cancelled) return
        if (typeof data.count === "number") {
          setActiveClientsCount(data.count)
        } else {
          setActiveClientsCount("failed")
        }
      })
      .catch(() => {
        if (!cancelled) setActiveClientsCount("failed")
      })
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    if (!paginationLoading && apiOrders.length > 0) {
      setDisplayedOrders(apiOrders)
    }
  }, [paginationLoading, apiOrders])

  useEffect(() => {
    if (apiOrders.length > 0 && displayedOrders.length === 0) {
      setDisplayedOrders(apiOrders)
    }
  }, [apiOrders, displayedOrders.length])

  useEffect(() => {
    if (user && (user.role === "org" || user.role === "organization" || user.role === "client")) {
      console.log("[v0] Non-admin user detected on admin dashboard, redirecting...")
      return
    }
  }, [user])

  const hasNoFilters = (search: string, status: string, client: string, business: string, userId: string) => {
    return !search && (!status || status === "all") && (!client || client === "all") && (!business || business === "all") && (!userId || userId === "all")
  }

  const handleSearchSubmit = () => {
    const value = searchInputValue.trim()
    setSearchQuery(value)
    setCurrentPage(1)

    if (hasNoFilters(value, statusFilter, clientFilter, businessFilter, userFilter) && initialOrders.length > 0) {
      setApiOrders(initialOrders)
      setDisplayedOrders(initialOrders)
      setTotalCount(initialOrdersCount)
      setTotalPages(Math.ceil(initialOrdersCount / pageSize))
      return
    }

    fetchOrders(1, pageSize, false, value, statusFilter, clientFilter, businessFilter, userFilter)
  }

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value)
    setCurrentPage(1)

    if (hasNoFilters(searchQuery, value, clientFilter, businessFilter, userFilter) && initialOrders.length > 0) {
      setApiOrders(initialOrders)
      setDisplayedOrders(initialOrders)
      setTotalCount(initialOrdersCount)
      setTotalPages(Math.ceil(initialOrdersCount / pageSize))
      return
    }

    fetchOrders(1, pageSize, false, searchQuery, value, clientFilter, businessFilter, userFilter)
  }

  const handleClientFilterChange = (value: string) => {
    setClientFilter(value)
    setBusinessFilter("all")
    setUserFilter("all")
    setCurrentPage(1)

    if (hasNoFilters(searchQuery, statusFilter, value, "all", "all") && initialOrders.length > 0) {
      setApiOrders(initialOrders)
      setDisplayedOrders(initialOrders)
      setTotalCount(initialOrdersCount)
      setTotalPages(Math.ceil(initialOrdersCount / pageSize))
      return
    }

    fetchOrders(1, pageSize, false, searchQuery, statusFilter, value, "all", "all")
  }

  const handleBusinessFilterChange = (value: string) => {
    setBusinessFilter(value)
    setCurrentPage(1)

    if (hasNoFilters(searchQuery, statusFilter, clientFilter, value, userFilter) && initialOrders.length > 0) {
      setApiOrders(initialOrders)
      setDisplayedOrders(initialOrders)
      setTotalCount(initialOrdersCount)
      setTotalPages(Math.ceil(initialOrdersCount / pageSize))
      return
    }

    fetchOrders(1, pageSize, false, searchQuery, statusFilter, clientFilter, value, userFilter)
  }

  const handleUserFilterChange = (value: string) => {
    setUserFilter(value)
    setCurrentPage(1)

    if (hasNoFilters(searchQuery, statusFilter, clientFilter, businessFilter, value) && initialOrders.length > 0) {
      setApiOrders(initialOrders)
      setDisplayedOrders(initialOrders)
      setTotalCount(initialOrdersCount)
      setTotalPages(Math.ceil(initialOrdersCount / pageSize))
      return
    }

    fetchOrders(1, pageSize, false, searchQuery, statusFilter, clientFilter, businessFilter, value)
  }

  const handleClearAllFilters = () => {
    setSearchQuery("")
    setSearchInputValue("")
    setStatusFilter("all")
    setClientFilter("all")
    setBusinessFilter("all")
    setUserFilter("all")
    setCurrentPage(1)

    if (initialOrders.length > 0) {
      setApiOrders(initialOrders)
      setDisplayedOrders(initialOrders)
      setTotalCount(initialOrdersCount)
      setTotalPages(Math.ceil(initialOrdersCount / pageSize))
    } else {
      fetchOrders(1, pageSize, false, "", "all", "all", "all", "all")
    }
  }

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage)
      fetchOrders(newPage, pageSize, true, searchQuery, statusFilter, clientFilter, businessFilter, userFilter)
    }
  }

  const handlePageSizeChange = (newSize: string) => {
    const size = Number.parseInt(newSize)
    setPageSize(size)
    setCurrentPage(1)
    fetchOrders(1, size, true, searchQuery, statusFilter, clientFilter, businessFilter, userFilter)
  }

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1
      setCurrentPage(newPage)
      fetchOrders(newPage, pageSize, true, searchQuery, statusFilter, clientFilter, businessFilter, userFilter)
    }
  }

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1
      setCurrentPage(newPage)
      fetchOrders(newPage, pageSize, true, searchQuery, statusFilter, clientFilter, businessFilter, userFilter)
    }
  }

  const handleFirstPage = () => {
    if (currentPage !== 1) {
      setCurrentPage(1)
      fetchOrders(1, pageSize, true, searchQuery, statusFilter, clientFilter, businessFilter, userFilter)
    }
  }

  const handleLastPage = () => {
    if (currentPage !== totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
      fetchOrders(totalPages, pageSize, true, searchQuery, statusFilter, clientFilter, businessFilter, userFilter)
    }
  }

  const behindScheduleOrders = mockOrders.filter((order) => {
    const dueDate = new Date(order.dueDate)
    const estimatedDate = new Date(order.estimatedCompletion)
    return order.linksPending > 0 && estimatedDate > dueDate
  })

  const totalPendingLinksOverdue = behindScheduleOrders.reduce((sum, order) => sum + order.linksPending, 0)

  const todayTasks = tasks.filter((task) => {
    const taskDate = new Date(task.dueDate)
    const today = new Date()
    return taskDate.toDateString() === today.toDateString() && task.status === "Pending"
  })

  const overdueTasks = tasks.filter((task) => {
    const taskDate = new Date(task.dueDate)
    const today = new Date()
    return taskDate < today && task.status === "Pending"
  })

  const highPriorityTasks = tasks.filter((task) => task.priority === "High" && task.status === "Pending")

  const filteredApiOrders = apiOrders

  // ZS-154: fetch the full order and open the create modal prepopulated with its values.
  const handleDuplicateOrder = async (order: ApiOrderLike) => {
    const orderId = order?.id != null ? String(order.id) : ""
    if (!orderId) return
    try {
      const raw = await httpJson<any>(`${CRM_API_BASE}/client_order/${orderId}/`)
      setDuplicateOrder(raw)
      setIsOrderModalOpen(true)
    } catch (error) {
      console.error("[v0] Error loading order to duplicate:", error)
      toast({
        title: "Could not duplicate order",
        description: "Failed to load the order's details. Please try again.",
        variant: "destructive",
      })
    }
  }

  const handleOrderSave = async (orderData: any) => {
    console.log("[v0] Order created successfully, refreshing orders list...")

    try {
      await fetchOrders(currentPage, pageSize)
      console.log("[v0] Orders list refreshed successfully")
    } catch (error) {
      console.error("[v0] Error refreshing orders list:", error)
    }

    const newOrder = {
      id: `ORD-${String(mockOrders.length + 1).padStart(3, "0")}`,
      client: orderData.client,
      campaign: `New Campaign ${mockOrders.length + 1}`,
      linksTarget: Number.parseInt(orderData.numberOfLinks) || 0,
      linksBuilt: 0,
      linksPending: Number.parseInt(orderData.numberOfLinks) || 0,
      dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      estimatedCompletion: new Date(Date.now() + 25 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
      status: "In Progress",
      priority: "Medium",
      daysRemaining: 30,
    }

    setMockOrders((prev) => [...prev, newOrder])
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Completed":
        return "bg-gradient-to-r from-emerald-50 to-green-50 text-emerald-700 border-emerald-200/50 shadow-sm"
      case "In Progress":
        return "bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 border-blue-200/50 shadow-sm"
      case "Behind Schedule":
        return "bg-gradient-to-r from-red-50 to-rose-50 text-red-700 border-red-200/50 shadow-sm"
      default:
        return "bg-gradient-to-r from-slate-50 to-gray-50 text-slate-700 border-slate-200/50 shadow-sm"
    }
  }

  const handleLogout = () => {
    logout()
  }

  const retryFetchOrders = () => {
    setOrdersError(null)
    fetchOrders(currentPage, pageSize)
  }
  return (
    <>
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <Separator orientation="vertical" className="mr-2 h-4" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage className="flex items-center gap-1">
                  <DashboardIcon className="h-4 w-4" />
                  Dashboard
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="ml-auto flex items-center gap-2 px-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-4 w-4" />
            <span>Welcome, {safeStringValue(user?.name || user?.email)}</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Logout
          </Button>
        </div>
      </header>
      <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
        <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
          <Card className="bg-gradient-to-br from-muted/50 to-slate-200/40 shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <CardTitle className="text-sm font-medium mb-2">Total Organizations</CardTitle>
                  {organizationsCount === null ? (
                    <div className="text-2xl font-bold drop-shadow-sm h-8 flex items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : organizationsCount === "failed" ? (
                    <div className="text-2xl font-bold drop-shadow-sm text-slate-400">
                      -
                    </div>
                  ) : (
                      <Link href="/organizations" className="block">
                        <div className="text-2xl font-bold drop-shadow-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors">
                          {organizationsCount.toLocaleString()}
                        </div>
                      </Link>
                  )}
                      <div className="flex justify-center mt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="shadow-sm border-gray-300 h-7 text-xs text-emerald-700 bg-white w-[140px]" 
                          disabled={organizationsCount === null || organizationsCount === "failed"}
                          onClick={() => setIsAddingOrganization(true)}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add Organization
                        </Button>
                      </div>
                </div>
                <Separator orientation="vertical" className="h-12" />
                <div className="flex-1">
                  <CardTitle className="text-sm font-medium mb-2">Total Opportunities</CardTitle>
                  {opportunitiesCount === null ? (
                    <div className="text-2xl font-bold drop-shadow-sm h-8 flex items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : opportunitiesCount === "failed" ? (
                    <div className="text-2xl font-bold drop-shadow-sm text-slate-400">
                      -
                    </div>
                  ) : (
                      <Link href="/opportunities" className="block">
                        <div className="text-2xl font-bold drop-shadow-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors">
                          {opportunitiesCount.toLocaleString()}
                        </div>
                      </Link>
                  )}
                      <div className="flex justify-center mt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="shadow-sm border-gray-300 h-7 text-xs text-emerald-700 bg-white w-[140px]" 
                          disabled={opportunitiesCount === null || opportunitiesCount === "failed"}
                          onClick={() => setIsAddingOpportunity(true)}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add Opportunity
                        </Button>
                      </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-muted/50 to-slate-200/40 shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <CardTitle className="text-sm font-medium mb-2">Total Clients</CardTitle>
                  {activeClientsCount === null ? (
                    <div className="text-2xl font-bold drop-shadow-sm h-8 flex items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : activeClientsCount === "failed" ? (
                    <div className="text-2xl font-bold drop-shadow-sm text-slate-400">
                      -
                    </div>
                  ) : (
                      <Link href="/clients" className="block">
                        <div className="text-2xl font-bold drop-shadow-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors">
                          {activeClientsCount.toLocaleString()}
                        </div>
                      </Link>
                  )}
                      <div className="flex justify-center mt-2">
                        <Button 
                          variant="outline"
                          size="sm" 
                          className="shadow-sm border-gray-300 h-7 text-xs text-emerald-700 bg-white w-[140px]" 
                      disabled={activeClientsCount === null || activeClientsCount === "failed"}
                          onClick={() => setIsAddingClient(true)}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add Client
                        </Button>
                      </div>
                </div>
                <Separator orientation="vertical" className="h-12" />
                <div className="flex-1">
                  <CardTitle className="text-sm font-medium mb-2">Total Businesses</CardTitle>
                  {businessesCount === null ? (
                    <div className="text-2xl font-bold drop-shadow-sm h-8 flex items-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : businessesCount === "failed" ? (
                    <div className="text-2xl font-bold drop-shadow-sm text-slate-400">
                      -
                    </div>
                  ) : (
                    <Link href="/businesses" className="block">
                      <div className="text-2xl font-bold drop-shadow-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors">
                        {businessesCount.toLocaleString()}
                      </div>
                    </Link>
                  )}
                      <div className="flex justify-center mt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="shadow-sm border-gray-300 h-7 text-xs text-emerald-700 bg-white w-[140px]" 
                          disabled={businessesCount === null || businessesCount === "failed"}
                          onClick={() => setIsAddingBusiness(true)}
                        >
                          <Plus className="mr-1 h-3 w-3" />
                          Add Business
                        </Button>
                      </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-muted/50 to-slate-200/40 shadow-md lg:col-span-2 order-first">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pt-4 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Orders
                {!isLoadingOrders && !Object.values(ordersCountByStatus).every(count => count === 0 || count === "failed") && (
                  <span className="text-muted-foreground font-normal">
                    {" "}({Object.values(ordersCountByStatus).reduce((sum, count) => sum + (typeof count === "number" ? count : 0), 0).toLocaleString()})
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-4">
              {isLoadingOrders || Object.values(ordersCountByStatus).every(count => count === 0 || count === "failed") ? (
                <div className="flex justify-start">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                <div className="flex gap-4 items-start">
                  {([
                    ["created", "Created"],
                    ["in_progress", "In Progress"],
                    ["paused", "Paused"],
                    ["cancelled", "Cancelled"],
                    ["complete", "Completed"],
                  ] as const)
                    .filter(([key]) => {
                      const count = ordersCountByStatus[key]
                      return count === "failed" || (typeof count === "number" && count > 0)
                    })
                    .map(([key, label], index, array) => {
                      const count = ordersCountByStatus[key]
                      return (
                        <div key={key} className="contents">
                          <div className="flex-1 flex flex-col">
                            {count === "failed" ? (
                              <div className="text-2xl font-bold drop-shadow-sm text-slate-400 mb-1">
                                -
                              </div>
                            ) : (
                              <Link href={`/orders?status=${key}`} className="block">
                                <div className="text-2xl font-bold drop-shadow-sm text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors mb-1">
                                  {count.toLocaleString()}
                                </div>
                              </Link>
                            )}
                            <CardTitle className="text-sm font-medium leading-tight">{label}</CardTitle>
                          </div>
                          {index < array.length - 1 && <Separator orientation="vertical" className="h-12 self-start" />}
                        </div>
                      )
                    })}
                </div>
              )}
              {ordersError && (ordersError.includes("deployment issues") || ordersError.includes("fallback")) && (
                <p className="text-xs text-muted-foreground mt-2">Fallback data (API issues)</p>
              )}
            </CardContent>
          </Card>
          {/* <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Validation Tasks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">n/a</div>
              <p className="text-xs text-muted-foreground">Pending API connection</p>
            </CardContent>
          </Card> */}
        </div>

        {/* Action Required Section */}
        {/* Pipeline Status Section */}
        {/* Today's Tasks Section */}

        {/* Order Management Section */}
        <div className="mt-6">
          <Collapsible open={ordersOpen} onOpenChange={setOrdersOpen}>
          <Card className="bg-muted/50 shadow-md">
            <CollapsibleTrigger asChild>
              <div className="cursor-pointer hover:bg-gradient-to-br hover:from-muted/50 hover:to-slate-200/30 transition-all duration-200 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <LinkIcon className="h-5 w-5 text-purple-500" />
                    <h2 className="text-lg font-semibold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                      Active Link Building Orders
                    </h2>
                    <Badge variant="outline" className="ml-2 drop-shadow-md font-semibold">
                      {isLoadingOrders ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        totalOrdersCount || totalCount
                      )}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <div onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shadow-sm border-gray-300 h-7 text-xs text-emerald-700 bg-white hover:bg-primary hover:text-primary-foreground"
                        onClick={() => {
                          setDuplicateOrder(null)
                          setIsOrderModalOpen(true)
                        }}
                      >
                        <Plus className="mr-1 h-3 w-3" />
                        Add New Order
                      </Button>
                    </div>
                    <Link href="/orders" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="outline"
                        size="sm"
                        className="shadow-sm border-gray-300 hover:bg-primary hover:text-primary-foreground h-7 text-xs bg-white"
                      >
                        <ExternalLink className="mr-1 h-3 w-3" />
                        View All Orders
                      </Button>
                    </Link>
                    {ordersOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground/80 mt-1">
                  Current campaigns with links built, pending, and timelines
                </p>
              </div>
            </CollapsibleTrigger>
            <CollapsibleContent className="overflow-visible">
              <CardContent className="pt-4 overflow-visible">
                {/* Modern Filters */}
                <div className="rounded-lg border bg-muted/50 p-4 mb-4 shadow-sm overflow-visible relative z-20 space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <form
                        className="flex flex-1 items-center gap-2 min-w-0 max-w-xl"
                        onSubmit={(e) => { e.preventDefault(); handleSearchSubmit() }}
                      >
                        <div className="relative flex-1 min-w-0 max-w-md">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                          <Input
                            placeholder="Search Orders…"
                            className="pl-8 pr-8 h-9"
                            value={searchInputValue}
                            onChange={(e) => setSearchInputValue(e.target.value)}
                            disabled={isLoadingOrders || paginationLoading}
                          />
                          {searchInputValue && (
                            <button
                              type="button"
                              onClick={() => { setSearchInputValue(""); if (searchQuery) handleClearAllFilters() }}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                        <Button type="submit" size="sm" className="h-9 shrink-0" disabled={isLoadingOrders || paginationLoading}>
                          Search
                        </Button>
                      </form>
                      <div className="ml-auto">
                        <DataTableColumnToggle
                          columns={columns}
                          onColumnChange={handleColumnChange}
                          onSelectAllColumns={handleSelectAllColumns}
                          showTip={false}
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs font-medium text-neutral-700">Client</Label>
                        <ClientFilterDropdown
                          value={clientFilter}
                          onChange={handleClientFilterChange}
                          disabled={isLoadingOrders || paginationLoading}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs font-medium text-neutral-700">Business</Label>
                        <BusinessFilterDropdown
                          value={businessFilter}
                          onChange={(id) => handleBusinessFilterChange(id)}
                          disabled={isLoadingOrders || paginationLoading}
                          clientId={clientFilter}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs font-medium text-neutral-700">User</Label>
                        <UserFilterDropdown
                          value={userFilter}
                          onChange={(id) => handleUserFilterChange(id)}
                          disabled={isLoadingOrders || paginationLoading}
                          clientId={clientFilter}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label className="text-xs font-medium text-neutral-700">Status</Label>
                        <Select value={statusFilter} onValueChange={handleStatusFilterChange} disabled={isLoadingOrders || paginationLoading}>
                          <SelectTrigger className="w-40 h-10 border border-neutral-300 bg-background">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="created">Created</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="paused">Paused</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                            <SelectItem value="complete">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {(searchQuery || statusFilter !== "all" || clientFilter !== "all" || businessFilter !== "all" || userFilter !== "all") && (
                        <Button variant="ghost" size="sm" onClick={handleClearAllFilters} className="h-9 self-end">
                          Clear filters
                        </Button>
                      )}
                    </div>
                </div>

                {/* Modern Table with Scrollable Container */}
                <Card className="border-0 shadow-sm bg-white/80 backdrop-blur-sm ring-1 ring-slate-200/50">
                  <CardContent className="p-0">
                    {ordersError ? (
                      <div className="p-8 text-center">
                        <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-4" />
                        <p className="text-sm text-red-600 mb-2">Error loading orders</p>
                        <p className="text-xs text-muted-foreground mb-4">{ordersError}</p>
                        <div className="space-y-2">
                          <Button
                            onClick={retryFetchOrders}
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                          >
                            Retry
                          </Button>
                          <p className="text-xs text-muted-foreground">
                            If the problem persists, please check your internet connection or contact support.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-md border overflow-x-auto">
                        <OrdersTable
                          orders={filteredApiOrders}
                          columns={columns}
                          isLoading={isLoadingOrders}
                          paginationLoading={paginationLoading}
                          emptyMessage="No orders found matching your criteria"
                          onDuplicateOrder={handleDuplicateOrder}
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
                <div className="mt-4">
                  <GlobalPagination
                    total={totalCount}
                    pageSize={pageSize}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    isLoading={isLoadingOrders || paginationLoading}
                    pageSizeOptions={[10, 20]}
                  />
                </div>
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
        </div>
      </div>

      {/* Order Creation Modal */}
      <CreateOrderForm
        isOpen={isOrderModalOpen}
        onClose={() => {
          setIsOrderModalOpen(false)
          setDuplicateOrder(null)
        }}
        onSave={handleOrderSave}
        asModal={true}
        duplicateFromOrder={duplicateOrder}
        initialClientId={
          duplicateOrder?.client?.id != null ? String(duplicateOrder.client.id) : undefined
        }
        initialBusinessId={
          duplicateOrder?.businesses?.[0]?.id != null
            ? String(duplicateOrder.businesses[0].id)
            : duplicateOrder?.business?.id != null
              ? String(duplicateOrder.business.id)
              : undefined
        }
      />

      {/* Add Client Modal */}
      {isAddingClient && (
        <ClientForm
          isOpen={isAddingClient}
          onClose={() => setIsAddingClient(false)}
          onSave={async (client) => {
            try {
              const token = typeof window !== "undefined" ? localStorage.getItem("auth_token") : null
              
              const logoFile = client.company_logo
              const addressData = client._addressData
              
              const clientDataWithoutLogoAndAddress = {
                legacy_id: client.legacy_id,
                budget: client.budget,
                link_count_requirements: client.link_count_requirements,
                email: client.email,
                company_name: client.company_name,
                first_name: client.first_name,
                last_name: client.last_name,
                phone: client.phone,
                domain: client.domain,
                locations: client.locations,
                metrics_requirements: client.metrics_requirements,
                users: client.users,
              }
              
              const response = await fetch(`${CRM_API_BASE}/client/`, {
                method: "POST",
                headers: {
                  'Content-Type': 'application/json',
                  ...(token && { Authorization: `Bearer ${token}` }),
                },
                body: JSON.stringify(clientDataWithoutLogoAndAddress),
              })

              if (!response.ok) {
                const errorData = await response.json()
                const error = new Error(`HTTP ${response.status}: ${JSON.stringify(errorData)}`)
                throw error
              }

              const createdClient = await response.json()

              const { id: portalUserId } = await postInternalClientUser(
                buildCreateInternalClientUserPayload({
                  email: client.email ?? "",
                  companyName: client.company_name,
                  firstName: client.first_name,
                  lastName: client.last_name,
                  phone: client.phone,
                  password: client.primary_user_password,
                  passwordConfirm: client.primary_user_password_confirm,
                }),
                token,
              )

              let addressId: number | null = null
              if (addressData) {
                try {
                  const cleanedAddressData = Object.fromEntries(
                    Object.entries(addressData).filter(([_, value]) => value !== "" && value !== null && value !== undefined)
                  )
                  
                  // Add tags with "primary" when creating address for a new client
                  cleanedAddressData.tags = ["primary"]
                  
                  const addressResponse = await httpJson(`${CRM_API_BASE}/address/`, {
                    method: "POST",
                    body: JSON.stringify(cleanedAddressData),
                  })

                  addressId = addressResponse.id
                } catch (addressError: any) {
                  console.error("Error creating address:", addressError)
                }
              }

              try {
                const patchBody: Record<string, unknown> = { users: [portalUserId] }
                if (addressId != null) patchBody.addresses = [addressId]
                await httpJson(`${CRM_API_BASE}/client/${createdClient.id}/`, {
                  method: "PATCH",
                  body: JSON.stringify(patchBody),
                })
              } catch (linkErr) {
                console.error("Error linking user/address to client:", linkErr)
              }

              if (logoFile) {
                try {
                  if (logoFile.type.startsWith("image/") && logoFile.size <= 5 * 1024 * 1024) {
                    const formData = new FormData()
                    formData.append("logo", logoFile)

                    await fetch(`${CRM_API_BASE}/client/${createdClient.id}/`, {
                      method: "PATCH",
                      headers: {
                        Authorization: `Bearer ${token}`,
                      },
                      body: formData,
                    })
                  }
                } catch (logoError) {
                  console.error("Error uploading logo:", logoError)
                }
              }

              router.push(`/clients/${createdClient.id}`)
            } catch (error: any) {
              if (error instanceof Error && error.message === "Request was cancelled") {
                return
              }
              throw error
            }
          }}
        />
      )}

      {/* Add Business Modal */}
      <AddBusinessModal
        isOpen={isAddingBusiness}
        onClose={() => setIsAddingBusiness(false)}
        onSuccess={async (businessId) => {
          await fetchBusinessesCount()
          router.push(`/businesses/${businessId}`)
        }}
      />

      <AddOrganizationModal
        open={isAddingOrganization}
        onClose={() => setIsAddingOrganization(false)}
        onSuccess={(organizationId) => {
          void fetchOrganizationsCount()
          router.push(`/organizations/${organizationId}`)
        }}
      />

      <AddOpportunityModal
        open={isAddingOpportunity}
        onClose={() => setIsAddingOpportunity(false)}
        onSuccess={(opportunityId) => {
          void fetchOpportunitiesCount()
          router.push(`/opportunities/${opportunityId}`)
        }}
      />

    </>
  )
}
